








































































import { Component, Vue, Prop, Emit } from 'vue-property-decorator';
import Icon from '../../generic/Icon.vue';
import Modal from '../../generic/Modal.vue';
import { getModule } from 'vuex-module-decorators';
import MainModule from '../../../store/modules/MainModule';
import { Source, Configuration, View } from 'ganttlab-entities';
import {
  ImplementedViewsGateways,
  ViewGateway,
} from '../../../helpers/ImplementedViewsGateways';
import { DisplayableError } from '../../../helpers/DisplayableError';
import { addDisplaybleError } from '../../../helpers';
import { getRememberedViewsBySource } from '../../../helpers/LocalForage';
import { trackInteractionEvent } from '../../../helpers/GTM';

const mainState = getModule(MainModule);

@Component({
  components: {
    Icon,
    Modal,
  },
})
export default class ViewSelector extends Vue {
  public showModal = false;
  public views: Array<ViewGateway> = [];
  public selectedViewToConfigure: ViewGateway | null = null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public configurator: any | null = null;

  @Prop({ required: true }) readonly sourceGateway!: Source;

  // view gateway is stored in vuex store
  get viewGateway(): View | null {
    if (mainState.viewGateway) {
      return mainState.viewGateway;
    }
    return null;
  }

  pickANewView() {
    this.showModal = true;
    trackInteractionEvent('Click', 'Pick a new view');
  }

  async selectNewView(newView: ViewGateway) {
    // let's see if the newly selected view has a configurator
    import(`./configurators/${newView.slug}.vue`)
      .then(() => {
        // display the newView configurator
        this.configurator = () => import(`./configurators/${newView.slug}.vue`);
        this.selectedViewToConfigure = newView;
        trackInteractionEvent(
          'View',
          'Selected (with configuration)',
          newView.slug,
        );
      })
      .catch(() => {
        if (this.viewGateway && newView.slug === this.viewGateway.slug) {
          // no configurator at all, and same view selected: just close the modal and nothing more
          this.closeModal();
          trackInteractionEvent('View', 'Selected same', newView.slug);
          return;
        }
        // another selected view without a configurator, just set it
        this.setView(newView, newView.defaultConfiguration);
        trackInteractionEvent(
          'View',
          'Selected (no configuration)',
          newView.slug,
        );
      });
  }

  closeModal() {
    this.showModal = false;
    this.selectedViewToConfigure = null;
    this.configurator = null;
  }

  setConfiguration(configuration: Configuration) {
    if (this.selectedViewToConfigure) {
      trackInteractionEvent(
        'View',
        'Configured',
        this.selectedViewToConfigure.slug,
      );
      this.setView(this.selectedViewToConfigure, configuration);
    }
  }

  @Emit('set-view')
  async setView(view: ViewGateway, configuration: Configuration) {
    // configure the view
    view.instance.setConfiguration(configuration);
    // store it in vuex
    mainState.setViewGateway(view.instance);
    // close the modal and emit the view
    this.closeModal();
    return view.instance;
  }

  async mounted() {
    const allViews = ImplementedViewsGateways;
    // ensure to put username on "mine" view
    allViews[0].defaultConfiguration.username = mainState.user?.username;

    this.views = allViews.filter((view: ViewGateway) => {
      return view.instance
        .supportedSourcesSlugs()
        .includes(this.sourceGateway.slug);
    });

    if (!this.views.length) {
      addDisplaybleError(
        new DisplayableError(
          `There is no view supporting the '${this.sourceGateway.slug}' source`,
          'Unable to list views',
        ),
      );

      return;
    }

    // if a remembered state allows for pre-setting a view, just do it now!
    const latestView = await getRememberedViewsBySource(
      this.sourceGateway.slug,
    );
    let viewGateway: ViewGateway | undefined;
    if (latestView) {
      viewGateway = allViews.find(
        (view: ViewGateway) => view.slug === latestView.slug,
      );
    }
    if (mainState.remember && latestView && viewGateway) {
      this.setView(viewGateway, latestView.configuration);
      trackInteractionEvent('View', 'Preset', viewGateway.slug);
    } else {
      // by default, simply use the first implemented view
      this.setView(this.views[0], this.views[0].defaultConfiguration);
      trackInteractionEvent('View', 'Default', this.views[0].slug);
    }
  }
}
